import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { featuredProjects } from "../data/site";

const typeFilters = [
    "All",
    "Residential Apartments",
    "Luxury Apartments",
    "Villa Community",
    "Commercial Complex",
    "Mixed Use",
];

export default function Projects() {
    const [filter, setFilter] = useState("All");
    const visible = useMemo(
        () =>
            filter === "All"
                ? featuredProjects
                : featuredProjects.filter((p) => p.type === filter),
        [filter],
    );

    return (
        <section
            id="projects"
            className="relative bg-[#0b0b0b] pt-16 md:pt-24 pb-24 md:pb-32 px-6 md:px-10"
            data-testid="projects-section"
        >
            <div className="mx-auto max-w-[1440px]">
                <div className="flex items-center gap-6 mb-10">
                    <span className="w-16 h-px bg-[#C89B3C]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                        Chapter 08 — The Portfolio
                    </span>
                </div>

                <div className="grid grid-cols-12 gap-6 items-end mb-14 md:mb-16">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9 }}
                        className="col-span-12 lg:col-span-8 font-display font-light text-white text-5xl md:text-6xl lg:text-7xl leading-[1] tracking-tight"
                    >
                        The <em className="text-[#E2C56D]">Portfolio.</em>
                    </motion.h2>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                        className="col-span-12 lg:col-span-4 text-[#bebebe] text-base leading-relaxed font-light"
                    >
                        An index of <span className="text-white">{featuredProjects.length} completed
                        projects</span> across Bangalore — from Berlie Street to Electronic
                        City, 1986 to today.
                    </motion.div>
                </div>

                {/* Filter chips */}
                <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-10 md:mb-12">
                    <span className="text-[10px] uppercase tracking-[0.35em] text-[#7a7a7a] mr-2">
                        Filter —
                    </span>
                    {typeFilters.map((t) => {
                        const active = filter === t;
                        return (
                            <button
                                key={t}
                                onClick={() => setFilter(t)}
                                className={`text-[10px] uppercase tracking-[0.28em] px-4 py-2 border transition-colors ${
                                    active
                                        ? "border-[#C89B3C] bg-[#C89B3C] text-[#0b0b0b]"
                                        : "border-[rgba(212,175,55,0.3)] text-white/85 hover:border-[#C89B3C] hover:text-[#E2C56D]"
                                }`}
                                data-testid={`project-filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {t}
                            </button>
                        );
                    })}
                </div>

                {/* Column headers */}
                <div
                    className="hidden md:grid grid-cols-12 gap-4 py-4 border-t border-b border-[rgba(212,175,55,0.2)] text-[10px] uppercase tracking-[0.35em] text-[#7a7a7a]"
                    aria-hidden
                >
                    <div className="col-span-1">№</div>
                    <div className="col-span-4">Project</div>
                    <div className="col-span-3">Type</div>
                    <div className="col-span-3">Location</div>
                    <div className="col-span-1 text-right">Year</div>
                </div>

                {/* Project rows */}
                <ul>
                    {visible.map((p, i) => (
                        <motion.li
                            key={p.no}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-5%" }}
                            transition={{ duration: 0.55, delay: (i % 10) * 0.04 }}
                            className="group relative grid grid-cols-12 gap-3 md:gap-4 py-6 md:py-7 border-b border-[rgba(212,175,55,0.15)] hover:border-[#C89B3C] transition-colors"
                            data-testid={`project-row-${p.no}`}
                        >
                            {/* Number */}
                            <div className="col-span-2 md:col-span-1 font-display text-[#C89B3C] text-base md:text-lg leading-none pt-1">
                                {String(p.no).padStart(2, "0")}
                            </div>

                            {/* Project name */}
                            <div className="col-span-10 md:col-span-4 flex items-center">
                                <div>
                                    <div className="font-display text-white text-2xl md:text-3xl lg:text-4xl leading-tight tracking-tight group-hover:text-[#E2C56D] transition-colors">
                                        {p.name}
                                    </div>
                                    {/* Mobile-only inline meta */}
                                    <div className="md:hidden mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.25em] text-[#bebebe]">
                                        <span>{p.type}</span>
                                        <span className="text-[#7a7a7a]">·</span>
                                        <span>{p.location}</span>
                                        <span className="text-[#7a7a7a]">·</span>
                                        <span className="text-[#E2C56D]">{p.year}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Type */}
                            <div className="hidden md:flex md:col-span-3 items-center text-[11px] uppercase tracking-[0.28em] text-[#bebebe] group-hover:text-white transition-colors">
                                {p.type}
                            </div>

                            {/* Location */}
                            <div className="hidden md:flex md:col-span-3 items-center text-[13px] font-light text-white/95">
                                <span className="w-2 h-2 rotate-45 border border-[#C89B3C] mr-3" />
                                {p.location}
                            </div>

                            {/* Year */}
                            <div className="hidden md:flex md:col-span-1 items-center justify-end font-display text-[#E2C56D] text-2xl md:text-3xl leading-none">
                                {p.year}
                            </div>

                            {/* Sliding gold underline */}
                            <span className="absolute left-0 bottom-[-1px] h-px w-0 bg-[#E2C56D] group-hover:w-full transition-[width] duration-700 ease-[cubic-bezier(0.2,0.7,0.2,1)]" />
                        </motion.li>
                    ))}
                </ul>

                {visible.length === 0 && (
                    <div className="py-16 text-center text-[#bebebe] font-light">
                        No projects match this filter yet.
                    </div>
                )}

                {/* Foot summary */}
                <div className="mt-12 md:mt-16 flex flex-wrap items-center justify-between gap-6 text-[10px] uppercase tracking-[0.35em] text-[#7a7a7a]">
                    <span className="flex items-center gap-3">
                        <span className="w-10 h-px bg-[#C89B3C]" />
                        Showing {visible.length} of {featuredProjects.length}
                    </span>
                    <span>
                        Earliest: 1986 &nbsp;·&nbsp; Latest: 2023 &nbsp;·&nbsp; Bangalore
                    </span>
                </div>
            </div>
        </section>
    );
}
